import { useEffect, useState } from "react";
import { Avatar, Card, LevelBadge, ProgressBar, Switch } from "@jaiho/ui";
import type { PlayerProfileDTO } from "@jaiho/shared";
import { useAuth } from "../state/AuthContext";
import { getMe, setIdentityVisibility } from "../api/endpoints";
import "./ProfileScreen.css";

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="jaiho-profile__stat">
      <div className="jaiho-profile__stat-value">{value}</div>
      <div className="jaiho-profile__stat-label">{label}</div>
    </div>
  );
}

export function ProfileScreen() {
  const { me: cachedMe, patchMe } = useAuth();
  const [me, setMe] = useState<PlayerProfileDTO | null>(cachedMe);
  const [savingIdentity, setSavingIdentity] = useState(false);

  useEffect(() => {
    void getMe().then(setMe);
  }, []);

  if (!me) return null;

  const hasPlayedAnyGame = me.stats.gamesPlayed > 0;

  async function handleToggleReveal(reveal: boolean) {
    setSavingIdentity(true);
    try {
      const updated = await setIdentityVisibility(reveal);
      setMe(updated);
      patchMe({ identity: updated.identity });
    } finally {
      setSavingIdentity(false);
    }
  }

  return (
    <div className="jaiho-profile">
      <header className="jaiho-profile__header">
        <div className="jaiho-profile__avatar-wrap">
          <Avatar name={me.profile.displayName} src={me.profile.avatarUrl} size={80} />
          <LevelBadge level={me.progression.level} />
        </div>
        <div className="jaiho-profile__name">{me.profile.displayName}</div>
        {me.profile.username && <div className="jaiho-profile__username">@{me.profile.username}</div>}
      </header>

      <Card className="jaiho-profile__level-card">
        <div className="jaiho-profile__level-top">
          <span>Level {me.progression.level}</span>
          <span className="jaiho-profile__level-xp">{me.progression.totalXp.toLocaleString("en-IN")} total XP</span>
        </div>
        <ProgressBar
          value={me.progression.progressRatio}
          label={`${me.progression.xpRemainingToNextLevel} XP to Level ${me.progression.level + 1}`}
        />
      </Card>

      <Card>
        <div className="jaiho-profile__row">
          <span>Balance</span>
          <span className="jaiho-profile__value">{me.wallet.balance.toLocaleString("en-IN")} credits</span>
        </div>
        <div className="jaiho-profile__row">
          <span>Daily streak</span>
          <span className="jaiho-profile__value">
            {me.streak.currentStreak} {me.streak.currentStreak === 1 ? "day" : "days"}
          </span>
        </div>
        <div className="jaiho-profile__row">
          <span>Achievements</span>
          <span className="jaiho-profile__value">{me.achievementsUnlockedCount} unlocked</span>
        </div>
        <div className="jaiho-profile__row">
          <span>Member since</span>
          <span className="jaiho-profile__value">
            {new Date(me.profile.createdAt).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" })}
          </span>
        </div>
      </Card>

      <section className="jaiho-profile__section">
        <div className="jaiho-profile__section-title">Game Stats</div>
        <Card>
          {hasPlayedAnyGame ? (
            <div className="jaiho-profile__stats-grid">
              <StatCell label="Played" value={me.stats.gamesPlayed.toLocaleString("en-IN")} />
              <StatCell label="Won" value={me.stats.gamesWon.toLocaleString("en-IN")} />
              <StatCell label="Wagered" value={me.stats.totalWagered.toLocaleString("en-IN")} />
              <StatCell label="Total won" value={me.stats.totalPayout.toLocaleString("en-IN")} />
            </div>
          ) : (
            <div className="jaiho-profile__stats-empty">
              No games played yet — stats will appear here once Jaiho91's games launch.
            </div>
          )}
        </Card>
      </section>

      <section className="jaiho-profile__section">
        <div className="jaiho-profile__section-title">Privacy</div>
        <Card className="jaiho-profile__settings-row">
          <div className="jaiho-profile__settings-copy">
            <div className="jaiho-profile__settings-label">Show my real name &amp; photo</div>
            <div className="jaiho-profile__settings-desc">
              {me.identity.revealIdentity
                ? `Other players see your Telegram name${me.identity.telegramDisplayName ? ` (${me.identity.telegramDisplayName})` : ""} and photo.`
                : `Other players see "${me.profile.displayName}" and no photo. Your real Telegram identity stays private.`}
            </div>
          </div>
          <Switch
            checked={me.identity.revealIdentity}
            onChange={handleToggleReveal}
            disabled={savingIdentity}
            label="Show my real name and photo to other players"
          />
        </Card>
      </section>

      <Card className="jaiho-profile__note">
        All credits are virtual, cannot be withdrawn or redeemed, and have no real-world monetary value.
      </Card>
    </div>
  );
}
